// ============================================================================
// OPTIMIZED MAIN CONTENT WITH HR MANUAL LEAVE ENTRY
// Path: frontend/src/components/dashboard/MainContent.tsx
// 
// NEW: Added HR Manual Leave Entry page route
// Route: 'hr-manual-leave-entry' opens HrManualLeaveEntryPage
// ============================================================================

import React, { 
  useMemo, 
  useTransition, 
  Suspense, 
  useCallback,
  useEffect,
  useState,
  useRef
} from 'react';
import { AuthUser } from '../../services/auth.service';
import styles from './MainContent.module.css';


// ============================================================================
// IMMEDIATE LOADING COMPONENTS (Critical Path)
// ============================================================================
import FloatingChatButton from './FloatingChatButton';

// ============================================================================
// PROGRESSIVE LAZY LOADING - PRIORITY SECTIONS
// ============================================================================

// HIGH PRIORITY - Load immediately but async
const GetStartedSection = React.lazy(() => 
  import('./sections/GetStartedSection').then(module => ({ 
    default: module.default 
  }))
);

const EmployeeNationalitySection = React.lazy(() => 
  import('./sections/EmployeeNationalitySection').then(module => ({ 
    default: module.default 
  }))
);

const EtisalatSimManagementPage = React.lazy(() => 
  import('./pages/EtisalatSimManagementPage')
);

const SecurityAuditLogsPage = React.lazy(() => 
  import('./pages/SecurityAuditLogsPage')
);

// MEDIUM PRIORITY - Load after critical sections
const ExpiringDocumentsSection = React.lazy(() => 
  Promise.all([
    import('./sections/ExpiringDocumentsSection'),
    new Promise(resolve => setTimeout(resolve, 100))
  ]).then(([module]) => ({ default: module.default }))
);

const UnregisteredEmployeesPage = React.lazy(() => 
  import('./pages/UnregisteredEmployeesPage')
);


const AttendanceAnomalyDetectorPage = React.lazy(() => 
  import('./pages/AttendanceAnomalyDetectorPage')
);

const LeaveReportPage = React.lazy(() => 
  import('./pages/LeaveReportPage')
);

const AbsentEmployeesPage = React.lazy(() => 
  import('./pages/AbsentEmployeesPage')
);

const ProjectLocationsMapSection = React.lazy(() => 
  Promise.all([
    import('./sections/ProjectLocationsMapSection'),
    new Promise(resolve => setTimeout(resolve, 200))
  ]).then(([module]) => ({ default: module.default }))
);

const CheckoutAuditLogsPage = React.lazy(() => 
  import('./pages/CheckoutAuditLogsPage')
);

// LOW PRIORITY - Load when needed or on scroll
const UpcomingCelebrationsSection = React.lazy(() => 
  import('./sections/UpcomingCelebrationsSection')
);

const RestTimingSchedulesSection = React.lazy(() => 
  import('./sections/RestTimingSchedulesSection')
);

const CelebrationPostsSection = React.lazy(() => 
  import('./sections/CelebrationPostsSection')
);

const HiringAnalyticsSection = React.lazy(() => 
  import('./sections/HiringAnalyticsSection')
);

const HRPayrollReportPage = React.lazy(() => 
  import('./pages/HRPayrollReportPage')
);


// ============================================================================
// ULTRA-LAZY LOADING - PAGES (Load on demand)
// ============================================================================

// Administrative pages - load only when accessed
const LocationManagementPage = React.lazy(() => 
  import('./pages/LocationManagementPage')
);

const PayrollSettingsPage = React.lazy(() => 
  import('./pages/PayrollSettingsPage')
);

const ScheduleTimePages = React.lazy(() => 
  import('./pages/ScheduleTimePages')
);

const OvertimeAccessPage = React.lazy(() => 
  import('./pages/OvertimeAccessPage')
);

const EmployeeListManagementPage = React.lazy(() => 
  import('./pages/EmployeeListManagementPage')
);

const LocationExemptionPage = React.lazy(() => 
  import('./pages/LocationExemptionPage')
);

const EmployeeCreationPage = React.lazy(() => 
  import('./pages/EmployeeCreationPage')
);

const AnnouncementManagementPage = React.lazy(() => 
  import('./pages/AnnouncementManagementPage')
);

const LineManagersPage = React.lazy(() => 
  import('./pages/LineManagersPage')
);

const NotificationsManagementPage = React.lazy(() => 
  import('./pages/NotificationsManagementPage')
);

const PayrollManagementPage = React.lazy(() => 
  import('./pages/PayrollManagementPage')
);

const AlternativeSaturdayManagementPage = React.lazy(() => 
  import('./pages/AlternativeSaturdayManagementPage')
);

const AnnualVacationsPage = React.lazy(() => 
  import('./pages/AnnualVacationsPage')
);

const EmployeeVacationBookingPage = React.lazy(() => 
  import('./pages/EmployeeVacationBookingPage')
);

const EmployeeNationalitiesPage = React.lazy(() => 
  import('./pages/EmployeeNationalitiesPage')
);

const HiringAnalyticsPage = React.lazy(() => 
  import('./pages/HiringAnalyticsPage')
);

const OvertimeReportsPage = React.lazy(() => 
  import('./pages/OvertimeReportsPage')
);

const ProjectManagementPage = React.lazy(() => 
  import('./pages/ProjectManagementPage')
);

const QRCodeGeneratorPage = React.lazy(() => 
  import('./pages/QRCodeGeneratorPage')
);

// MANPOWER COMPONENTS
const ManpowerAllocationPage = React.lazy(() => 
  import('./pages/ManpowerAllocationPage')
);

const ManpowerDashboard = React.lazy(() => 
  import('./pages/ManpowerDashboard')
);

// HR LEAVE APPROVAL PAGE - Multi-Level Approval Workflow
const HrLeaveApprovalPage = React.lazy(() => 
  import('./pages/HrLeaveApprovalPage')
);

// ANNUAL LEAVE MANAGEMENT PAGE - Excel-style with HR Manual Entry
const AnnualLeaveManagementPage = React.lazy(() => 
  import('./pages/AnnualLeaveManagementPageNew')
);

// NEW: HR MANUAL LEAVE ENTRY PAGE - Full Leave Application System
const HrManualLeaveEntryPage = React.lazy(() => 
  import('./pages/HrManualLeaveEntryPage')
);

// DATA ACCURACY REPORT PAGE
const DataAccuracyReportPage = React.lazy(() => 
  import('./pages/DataAccuracyReportPage')
);

// Heavy components - aggressive lazy loading
const AttendancePage = React.lazy(() => 
  import('./pages/AttendancePage')
);

const ManageEmployeePage = React.lazy(() => 
  import('./pages/ManageEmployeePage')
);

const AddEmployeePage = React.lazy(() => 
  import('./pages/AddEmployeePage')
);

const EmployeeProfilePage = React.lazy(() => 
  import('./pages/EmployeeProfilePage')
);

const LeaveManagementPage = React.lazy(() => 
  import('./pages/LeaveManagementPage')
);

const NightShiftManagementPage = React.lazy(() => 
  import('./pages/NightShiftManagementPage')
);

const ScheduleTimePage = React.lazy(() => 
  import('./pages/ScheduleTimePage')
);

// ============================================================================
// SKELETON LOADING COMPONENTS - INSTANT UI FEEDBACK
// ============================================================================

const DashboardSkeleton: React.FC = React.memo(() => (
  <div className={styles.contentGrid}>
    <div className={styles.leftColumn}>
      <div className={styles.skeletonSection}>
        <div className={styles.skeletonHeader}></div>
        <div className={styles.skeletonContent}></div>
      </div>
      <div className={styles.skeletonSection}>
        <div className={styles.skeletonHeader}></div>
        <div className={styles.skeletonMap}></div>
      </div>
    </div>
    <div className={styles.rightColumn}>
      <div className={styles.skeletonSection}>
        <div className={styles.skeletonHeader}></div>
        <div className={styles.skeletonCards}></div>
      </div>
      <div className={styles.skeletonSection}>
        <div className={styles.skeletonHeader}></div>
        <div className={styles.skeletonList}></div>
      </div>
    </div>
  </div>
));
DashboardSkeleton.displayName = 'DashboardSkeleton';

const PageSkeleton: React.FC = React.memo(() => (
  <div className={styles.pageLayout}>
    <div className={styles.skeletonPageHeader}></div>
    <div className={styles.skeletonPageContent}></div>
  </div>
));
PageSkeleton.displayName = 'PageSkeleton';

// ============================================================================
// PERFORMANCE OPTIMIZED MAIN COMPONENT
// ============================================================================

export interface MainContentProps {
  activePage: string;
  employeeId?: string;
  onNavigate?: (page: string, employeeId?: string) => void;
  isTransitioning?: boolean;
  isSidebarCollapsed?: boolean;
  user?: AuthUser;
}

const MainContent: React.FC<MainContentProps> = React.memo(({ 
  activePage, 
  employeeId,
  onNavigate,
  isTransitioning = false, 
  isSidebarCollapsed = false,
  user
}) => {
  const [isPending, startTransition] = useTransition();
  const [sectionsLoaded, setSectionsLoaded] = useState(false);
  const [shouldPreload, setShouldPreload] = useState(false);
  const intersectionRef = useRef<IntersectionObserver | null>(null);

  // ============================================================================
  // PERFORMANCE: Navigation with transition
  // ============================================================================
  const handleNavigate = useCallback((page: string, empId?: string) => {
    if (onNavigate) {
      startTransition(() => {
        onNavigate(page, empId);
      });
    }
  }, [onNavigate]);

  // ============================================================================
  // PERFORMANCE: Memoized user admin check
  // ============================================================================
  const isUserAdmin = useMemo(() => {
    return user?.isAdmin === true;
  }, [user?.isAdmin]);

  // ============================================================================
  // PERFORMANCE: Progressive section loading
  // ============================================================================
  useEffect(() => {
    if (activePage === 'dashboard') {
      // Load critical sections immediately
      const timer = setTimeout(() => {
        setSectionsLoaded(true);
      }, 50);

      // Preload common pages after dashboard loads
      const preloadTimer = setTimeout(() => {
        setShouldPreload(true);
      }, 1000);

      return () => {
        clearTimeout(timer);
        clearTimeout(preloadTimer);
      };
    }
  }, [activePage]);

  // ============================================================================
  // PERFORMANCE: Preload critical pages
  // ============================================================================
  useEffect(() => {
    if (shouldPreload) {
      // Preload most commonly accessed pages
      const preloadPages = [
        () => import('./pages/AttendancePage'),
        () => import('./pages/ManageEmployeePage'),
        () => import('./pages/EmployeeProfilePage'),
        () => import('./pages/LeaveManagementPage'),
        () => import('./pages/ManpowerAllocationPage'),
        () => import('./pages/ManpowerDashboard'),
        () => import('./pages/AnnualLeaveManagementPageNew'),
        () => import('./pages/HrLeaveApprovalPage'),
        () => import('./pages/components/AnnualLeaveManagementPage'),
        () => import('./pages/HrManualLeaveEntryPage'), // Preload HR Manual Leave Entry
        () => import('./pages/DataAccuracyReportPage'),
        () => import('./pages/CheckoutAuditLogsPage'),
        () => import('./pages/AttendanceAnomalyDetectorPage'),
      ];

      preloadPages.forEach((importFn, index) => {
        setTimeout(() => {
          importFn().catch(() => {});
        }, index * 200);
      });
    }
  }, [shouldPreload]);

  // ============================================================================
  // PERFORMANCE: Optimized navigation handlers
  // ============================================================================
  const handleEmployeeClick = useCallback((employeeId: string) => {
    handleNavigate('employee-profile', employeeId);
  }, [handleNavigate]);

  const handleEmployeeVacationClick = useCallback((employeeId: string) => {
    handleNavigate('employee-vacation-booking', employeeId);
  }, [handleNavigate]);

  // ============================================================================
  // PERFORMANCE: Memoized card click handlers for reports dashboard
  // ============================================================================
  const handleOvertimeReportsClick = useCallback(() => {
    handleNavigate('overtime-reports');
  }, [handleNavigate]);

  const handleManpowerDashboardClick = useCallback(() => {
    handleNavigate('manpower-dashboard');
  }, [handleNavigate]);

  const handleAttendanceClick = useCallback(() => {
    handleNavigate('attendance');
  }, [handleNavigate]);

  const handleDataAccuracyClick = useCallback(() => {
    handleNavigate('data-accuracy-report');
  }, [handleNavigate]);

  // ============================================================================
  // PERFORMANCE: Memoized style objects
  // ============================================================================
  const reportCardStyles = useMemo(() => ({
    base: {
      padding: '24px',
      borderRadius: '12px',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      textAlign: 'center' as const
    },
    overtimeCard: {
      backgroundColor: 'var(--color-primary)',
      color: 'white'
    },
    manpowerCard: {
      backgroundColor: '#10b981',
      color: 'white'
    },
    attendanceCard: {
      backgroundColor: 'white',
      color: 'var(--color-primary)',
      border: '2px solid var(--color-primary)'
    },
    dataAccuracyCard: {
      backgroundColor: '#722ed1',
      color: 'white'
    }
  }), []);

  const reportCardHoverHandlers = useMemo(() => ({
    onMouseEnter: (e: React.MouseEvent<HTMLDivElement>) => {
      e.currentTarget.style.transform = 'translateY(-2px)';
      e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.15)';
    },
    onMouseLeave: (e: React.MouseEvent<HTMLDivElement>) => {
      e.currentTarget.style.transform = 'translateY(0)';
      e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
    }
  }), []);

  const attendanceCardHoverHandlers = useMemo(() => ({
    onMouseEnter: (e: React.MouseEvent<HTMLDivElement>) => {
      e.currentTarget.style.transform = 'translateY(-2px)';
      e.currentTarget.style.backgroundColor = 'var(--color-primary-50)';
    },
    onMouseLeave: (e: React.MouseEvent<HTMLDivElement>) => {
      e.currentTarget.style.transform = 'translateY(0)';
      e.currentTarget.style.backgroundColor = 'white';
    }
  }), []);

  // ============================================================================
  // PERFORMANCE: Dashboard sections with progressive loading
  // ============================================================================
  const dashboardSections = useMemo(() => {
    if (!sectionsLoaded) {
      return <DashboardSkeleton />;
    }

    return (
      <>
        <div className={styles.leftColumn}>
          <Suspense fallback={<div className={styles.sectionSkeleton}></div>}>
            <GetStartedSection user={user} />
          </Suspense>
          
          <Suspense fallback={<div className={styles.sectionSkeleton}></div>}>
            <ProjectLocationsMapSection />
          </Suspense>
          
          <Suspense fallback={<div className={styles.sectionSkeleton}></div>}>
            <HiringAnalyticsSection onNavigate={handleNavigate} />
          </Suspense>
          
          <Suspense fallback={<div className={styles.sectionSkeleton}></div>}>
            <CelebrationPostsSection onNavigate={handleNavigate} />
          </Suspense>
        </div>
        
        <div className={styles.rightColumn}>
          <Suspense fallback={<div className={styles.sectionSkeleton}></div>}>
            <ExpiringDocumentsSection />
          </Suspense>
          
          <Suspense fallback={<div className={styles.sectionSkeleton}></div>}>
            <EmployeeNationalitySection onNavigate={handleNavigate} />
          </Suspense>
          
          <Suspense fallback={<div className={styles.sectionSkeleton}></div>}>
            <UpcomingCelebrationsSection onNavigate={handleNavigate} />
          </Suspense>
          
          <Suspense fallback={<div className={styles.sectionSkeleton}></div>}>
            <RestTimingSchedulesSection onNavigate={handleNavigate} />
          </Suspense>
        </div>
      </>
    );
  }, [sectionsLoaded, user, handleNavigate]);

  // ============================================================================
  // PERFORMANCE: Optimized page key generation
  // ============================================================================
  const pageKey = useMemo(() => 
    `${activePage}-${employeeId || 'default'}`, 
    [activePage, employeeId]
  );

  // ============================================================================
  // PERFORMANCE: Optimized page rendering with smart loading
  // ============================================================================
  const renderPageContent = useMemo(() => {
    switch (activePage) {
      // ================================================================
      // NEW: HR MANUAL LEAVE ENTRY PAGE
      // Full leave application system with travel details for annual leave
      // ================================================================
      case 'hr-manual-leave-entry':
        return (
          <div className={`${styles.pageLayout} ${isTransitioning ? styles.transitioning : ''}`} key={pageKey}>
            <Suspense fallback={<PageSkeleton />}>
              <HrManualLeaveEntryPage 
                user={user}
                onNavigate={handleNavigate}
                onBack={() => handleNavigate('hr-leave-approvals')}
              />
            </Suspense>
          </div>
        );

      // ================================================================
      // DATA ACCURACY REPORT PAGE
      // Identifies problematic attendance records for payroll review
      // ================================================================
      case 'data-accuracy-report':
        return (
          <div className={`${styles.pageLayout} ${isTransitioning ? styles.transitioning : ''}`} key={pageKey}>
            <Suspense fallback={<PageSkeleton />}>
              <DataAccuracyReportPage 
                onBack={() => handleNavigate('dashboard')}
                onNavigate={handleNavigate}
                user={user}
              />
            </Suspense>
          </div>
        );

      // ================================================================
      // ANNUAL LEAVE MANAGEMENT - Excel-style with HR Manual Entry
      // ================================================================
      case 'annual-leave-management':
        return (
          <div className={`${styles.pageLayout} ${isTransitioning ? styles.transitioning : ''}`} key={pageKey}>
            <Suspense fallback={<PageSkeleton />}>
              <AnnualLeaveManagementPage 
                user={user}
              />
            </Suspense>
          </div>
        );

      // ================================================================
      // HR LEAVE APPROVALS - Multi-Level Approval Workflow
      // ================================================================
      case 'hr-leave-approvals':
        return (
          <div className={`${styles.pageLayout} ${isTransitioning ? styles.transitioning : ''}`} key={pageKey}>
            <Suspense fallback={<PageSkeleton />}>
              <HrLeaveApprovalPage 
                hrManagerId={user?.uid || ''}
                hrManagerName={user?.displayName || user?.email || 'HR Manager'}
              />
            </Suspense>
          </div>
        );

      // ================================================================
      // MANPOWER - MAIN ENTRY POINT (Project Allocation Page)
      // ================================================================
      case 'manpower-dashboard':
        return (
          <div className={`${styles.pageLayout} ${isTransitioning ? styles.transitioning : ''}`} key={pageKey}>
            <Suspense fallback={<PageSkeleton />}>
              <ManpowerAllocationPage 
                onBack={() => handleNavigate('dashboard')}
                onNavigate={handleNavigate}
                user={user}
              />
            </Suspense>
          </div>
        );


        case 'checkout-audit-logs':
        return (
          <div className={`${styles.pageLayout} ${isTransitioning ? styles.transitioning : ''}`} key={pageKey}>
            <Suspense fallback={<PageSkeleton />}>
              <CheckoutAuditLogsPage 
                onBack={() => handleNavigate('dashboard')}
                onNavigate={handleNavigate}
              />
            </Suspense>
          </div>
        );

      case 'leave-report':
        return (
          <div className={`${styles.pageLayout} ${isTransitioning ? styles.transitioning : ''}`} key={pageKey}>
            <Suspense fallback={<PageSkeleton />}>
              <LeaveReportPage 
                onBack={() => handleNavigate('dashboard')}
                user={user}
              />
            </Suspense>
          </div>
        );

      // ================================================================
      // MANPOWER - MASON VIEW (Secondary - accessed from Allocation)
      // ================================================================
      case 'manpower-masons':
        return (
          <div className={`${styles.pageLayout} ${isTransitioning ? styles.transitioning : ''}`} key={pageKey}>
            <Suspense fallback={<PageSkeleton />}>
              <ManpowerDashboard 
                onBack={() => handleNavigate('manpower-dashboard')}
                onNavigate={handleNavigate}
                user={user}
              />
            </Suspense>
          </div>
        );

      // ================================================================
      // MANPOWER - Direct Allocation (for specific manager)
      // ================================================================
      case 'manpower-allocation':
        return (
          <div className={`${styles.pageLayout} ${isTransitioning ? styles.transitioning : ''}`} key={pageKey}>
            <Suspense fallback={<PageSkeleton />}>
              <ManpowerAllocationPage 
                onBack={() => handleNavigate('manpower-dashboard')}
                onNavigate={handleNavigate}
                user={user}
                initialManagerId={employeeId}
              />
            </Suspense>
          </div>
        );

      case 'project-management':
        return (
          <div className={`${styles.pageLayout} ${isTransitioning ? styles.transitioning : ''}`} key={pageKey}>
            <Suspense fallback={<PageSkeleton />}>
              <ProjectManagementPage 
                onBack={() => handleNavigate('dashboard')}
                onNavigate={handleNavigate}
                user={user}
              />
            </Suspense>
          </div>
        );



      case 'attendance-anomaly-detector':
        return (
          <div className={`${styles.pageLayout} ${isTransitioning ? styles.transitioning : ''}`} key={pageKey}>
            <Suspense fallback={<PageSkeleton />}>
              <AttendanceAnomalyDetectorPage 
                onBack={() => handleNavigate('hr-payroll-report')}
                onNavigate={handleNavigate}
                user={user}
              />
            </Suspense>
          </div>
        );

      case 'qr-code-generator':
        return (
          <div className={`${styles.pageLayout} ${isTransitioning ? styles.transitioning : ''}`} key={pageKey}>
            <Suspense fallback={<PageSkeleton />}>
              <QRCodeGeneratorPage 
                onBack={() => handleNavigate('dashboard')}
                user={user}
              />
            </Suspense>
          </div>
        );


        case 'etisalat-sim-management':
  return (
    <div className={`${styles.pageLayout} ${isTransitioning ? styles.transitioning : ''}`} key={pageKey}>
      <Suspense fallback={<PageSkeleton />}>
        <EtisalatSimManagementPage 
          onBack={() => handleNavigate('dashboard')}
        />
      </Suspense>
    </div>
  );

      case 'overtime-reports':
        return (
          <div className={`${styles.pageLayout} ${isTransitioning ? styles.transitioning : ''}`} key={pageKey}>
            <Suspense fallback={<PageSkeleton />}>
              <OvertimeReportsPage 
                
              />
            </Suspense>
          </div>
        );

      case 'hr-payroll-report':
        return (
          <div className={`${styles.pageLayout} ${isTransitioning ? styles.transitioning : ''}`} key={pageKey}>
            <Suspense fallback={<PageSkeleton />}>
              <HRPayrollReportPage 
                onBack={() => handleNavigate('dashboard')}
                user={user}
              />
            </Suspense>
          </div>
        );

      case 'hiring-analytics':
        return (
          <div className={`${styles.pageLayout} ${isTransitioning ? styles.transitioning : ''}`} key={pageKey}>
            <Suspense fallback={<PageSkeleton />}>
              <HiringAnalyticsPage 
                onBack={() => handleNavigate('dashboard')}
                onNavigate={handleNavigate}
              />
            </Suspense>
          </div>
        );

      case 'hiring-analytics-year':
        return (
          <div className={`${styles.pageLayout} ${isTransitioning ? styles.transitioning : ''}`} key={pageKey}>
            <Suspense fallback={<PageSkeleton />}>
              <HiringAnalyticsPage 
                onBack={() => handleNavigate('hiring-analytics')}
                onNavigate={handleNavigate}
                initialYear={parseInt(employeeId || '0')}
              />
            </Suspense>
          </div>
        );

      case 'hiring-analytics-month':
        return (
          <div className={`${styles.pageLayout} ${isTransitioning ? styles.transitioning : ''}`} key={pageKey}>
            <Suspense fallback={<PageSkeleton />}>
              <HiringAnalyticsPage 
                onBack={() => handleNavigate('hiring-analytics-year', employeeId?.split('-')[0])}
                onNavigate={handleNavigate}
                initialYear={parseInt(employeeId?.split('-')[0] || '0')}
                initialMonth={employeeId?.split('-')[1]}
              />
            </Suspense>
          </div>
        );

      case 'employee-nationalities':
        return (
          <div className={`${styles.pageLayout} ${isTransitioning ? styles.transitioning : ''}`} key={pageKey}>
            <Suspense fallback={<PageSkeleton />}>
              <EmployeeNationalitiesPage 
                onBack={() => handleNavigate('dashboard')}
                onNavigate={handleNavigate}
              />
            </Suspense>
          </div>
        );

      case 'attendance':
        return (
          <div className={`${styles.pageLayout} ${isTransitioning ? styles.transitioning : ''}`} key={pageKey}>
            <Suspense fallback={<PageSkeleton />}>
              <AttendancePage 
                onNavigate={handleNavigate}
              />
            </Suspense>
          </div>
        );
      
      case 'employees':
        return (
          <div className={`${styles.pageLayout} ${isTransitioning ? styles.transitioning : ''}`} key={pageKey}>
            <Suspense fallback={<PageSkeleton />}>
              <ManageEmployeePage 
                onAddEmployee={() => handleNavigate('add-employee')}
                onEmployeeClick={handleEmployeeClick}
                onNavigate={handleNavigate}
              />
            </Suspense>
          </div>
        );

      case 'add-employee':
        return (
          <div className={`${styles.pageLayout} ${isTransitioning ? styles.transitioning : ''}`} key={pageKey}>
            <Suspense fallback={<PageSkeleton />}>
              <AddEmployeePage 
                onBack={() => handleNavigate('employees')}
                onSuccess={() => {
                  setTimeout(() => handleNavigate('employees'), 2000);
                }}
              />
            </Suspense>
          </div>
        );
      
      case 'employee-profile':
        return (
          <div className={`${styles.pageLayout} ${isTransitioning ? styles.transitioning : ''}`} key={pageKey}>
            <Suspense fallback={<PageSkeleton />}>
              <EmployeeProfilePage 
                employeeId={employeeId}
                onBack={() => handleNavigate('employees')}
              />
            </Suspense>
          </div>
        );
        

        case 'app-announcements':
  return (
    <div className={`${styles.pageLayout} ${isTransitioning ? styles.transitioning : ''}`} key={pageKey}>
      <Suspense fallback={<PageSkeleton />}>
        <AnnouncementManagementPage 
          onBack={() => handleNavigate('dashboard')}
          user={user}
        />
      </Suspense>
    </div>
  );


  

      case 'schedule-time':
        return (
          <div className={`${styles.pageLayout} ${isTransitioning ? styles.transitioning : ''}`} key={pageKey}>
            <Suspense fallback={<PageSkeleton />}>
              <ScheduleTimePage 
                onBack={() => handleNavigate('dashboard')}
                onNavigate={handleNavigate}
              />
            </Suspense>
          </div>
        );

      case 'Work-timing':
        return (
          <div className={`${styles.pageLayout} ${isTransitioning ? styles.transitioning : ''}`} key={pageKey}>
            <Suspense fallback={<PageSkeleton />}>
              <ScheduleTimePages 
                onBack={() => handleNavigate('dashboard')}
                onNavigate={handleNavigate}
              />
            </Suspense>
          </div>
        );
      
      case 'alternative-saturday':
        return (
          <div className={`${styles.pageLayout} ${isTransitioning ? styles.transitioning : ''}`} key={pageKey}>
            <Suspense fallback={<PageSkeleton />}>
              <AlternativeSaturdayManagementPage 
                onBack={() => handleNavigate('dashboard')}
                user={user}
              />
            </Suspense>
          </div>
        );
      
      case 'payroll':
        return (
          <div className={`${styles.pageLayout} ${isTransitioning ? styles.transitioning : ''}`} key={pageKey}>
            <Suspense fallback={<PageSkeleton />}>
              <PayrollManagementPage 
                onBack={() => handleNavigate('dashboard')}
                onNavigate={handleNavigate}
                user={user}
              />
            </Suspense>
          </div>
        );

      case 'payroll-settings':
        return (
          <div className={`${styles.pageLayout} ${isTransitioning ? styles.transitioning : ''}`} key={pageKey}>
            <Suspense fallback={<PageSkeleton />}>
              <PayrollSettingsPage 
                onBack={() => handleNavigate('dashboard')}
                user={user}
              />
            </Suspense>
          </div>
        );
      
      case 'reports':
        return (
          <div className={`${styles.pageLayout} ${isTransitioning ? styles.transitioning : ''}`} key={pageKey}>
            <div className={styles.placeholderPage}>
              <h2>Reports Dashboard</h2>
              <p>Choose from available report types to view comprehensive analytics:</p>
              <div style={{ 
                marginTop: '30px', 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
                gap: '20px',
                maxWidth: '1000px'
              }}>
                <div 
                  onClick={handleOvertimeReportsClick}
                  style={{ 
                    ...reportCardStyles.base,
                    ...reportCardStyles.overtimeCard
                  }}
                  {...reportCardHoverHandlers}
                >
                  <h3 style={{ margin: '0 0 8px 0', fontSize: '1.2em' }}>Overtime Reports</h3>
                  <p style={{ margin: 0, fontSize: '0.9em', opacity: 0.9 }}>
                    Comprehensive overtime request management and analytics
                  </p>
                </div>
                
                <div 
                  onClick={handleManpowerDashboardClick}
                  style={{ 
                    ...reportCardStyles.base,
                    ...reportCardStyles.manpowerCard
                  }}
                  {...reportCardHoverHandlers}
                >
                  <h3 style={{ margin: '0 0 8px 0', fontSize: '1.2em' }}>Manpower Allocation</h3>
                  <p style={{ margin: 0, fontSize: '0.9em', opacity: 0.9 }}>
                    Project allocation management and workforce analytics
                  </p>
                </div>
                
                <div 
                  onClick={handleAttendanceClick}
                  style={{ 
                    ...reportCardStyles.base,
                    ...reportCardStyles.attendanceCard
                  }}
                  {...attendanceCardHoverHandlers}
                >
                  <h3 style={{ margin: '0 0 8px 0', fontSize: '1.2em' }}>Attendance Reports</h3>
                  <p style={{ margin: 0, fontSize: '0.9em', opacity: 0.8 }}>
                    Employee attendance tracking and analysis
                  </p>
                </div>

                {/* Data Accuracy Report Card */}
                <div 
                  onClick={handleDataAccuracyClick}
                  style={{ 
                    ...reportCardStyles.base,
                    ...reportCardStyles.dataAccuracyCard
                  }}
                  {...reportCardHoverHandlers}
                >
                  <h3 style={{ margin: '0 0 8px 0', fontSize: '1.2em' }}>Data Accuracy Report</h3>
                  <p style={{ margin: 0, fontSize: '0.9em', opacity: 0.9 }}>
                    Identify problematic attendance records for payroll review
                  </p>
                </div>
              </div>
            </div>
          </div>
        );
      
      case 'location':
        return (
          <div className={`${styles.pageLayout} ${isTransitioning ? styles.transitioning : ''}`} key={pageKey}>
            <Suspense fallback={<PageSkeleton />}>
              <LocationManagementPage />
            </Suspense>
          </div>
        );
      
      case 'leave':
      case 'leave-applications':
        return (
          <div className={`${styles.pageLayout} ${isTransitioning ? styles.transitioning : ''}`} key={pageKey}>
            <Suspense fallback={<PageSkeleton />}>
              <LeaveManagementPage />
            </Suspense>
          </div>
        );

      case 'annual-vacations':
        return (
          <div className={`${styles.pageLayout} ${isTransitioning ? styles.transitioning : ''}`} key={pageKey}>
            <Suspense fallback={<PageSkeleton />}>
              <AnnualVacationsPage 
                onEmployeeClick={handleEmployeeVacationClick}
                onNavigate={handleNavigate}
              />
            </Suspense>
          </div>
        );

      case 'night-shift-management':
        return (
          <div className={`${styles.pageLayout} ${isTransitioning ? styles.transitioning : ''}`} key={pageKey}>
            <Suspense fallback={<PageSkeleton />}>
              <NightShiftManagementPage 
                onBack={() => handleNavigate('dashboard')}
                user={user}
              />
            </Suspense>
          </div>
        );

      case 'employee-vacation-booking':
        return (
          <div className={`${styles.pageLayout} ${isTransitioning ? styles.transitioning : ''}`} key={pageKey}>
            <Suspense fallback={<PageSkeleton />}>
              <EmployeeVacationBookingPage 
                employeeId={employeeId}
                onBack={() => handleNavigate('annual-vacations')}
                onNavigate={handleNavigate}
              />
            </Suspense>
          </div>
        );

      case 'celebrations':
        return (
          <div className={`${styles.pageLayout} ${isTransitioning ? styles.transitioning : ''}`} key={pageKey}>
            <div className={styles.placeholderPage}>
              <h2>All Celebrations</h2>
              <p>Complete celebrations calendar and management will be implemented here.</p>
            </div>
          </div>
        );
      
      case 'system-settings':
        return (
          <div className={`${styles.pageLayout} ${isTransitioning ? styles.transitioning : ''}`} key={pageKey}>
            <div className={styles.placeholderPage}>
              <h2>System Settings</h2>
              <p>System configuration options will be implemented here.</p>
            </div>
          </div>
        );

      case 'notifications':
        return (
          <div className={`${styles.pageLayout} ${isTransitioning ? styles.transitioning : ''}`} key={pageKey}>
            <Suspense fallback={<PageSkeleton />}>
              <NotificationsManagementPage 
                onBack={() => handleNavigate('dashboard')}
              />
            </Suspense>
          </div>
        );
      
      case 'line-managers':
        return (
          <div className={`${styles.pageLayout} ${isTransitioning ? styles.transitioning : ''}`} key={pageKey}>
            <Suspense fallback={<PageSkeleton />}>
              <LineManagersPage
                onBack={() => handleNavigate('dashboard')}
              />
            </Suspense>
          </div>
        );
      
      case 'overtime-access':
        return (
          <div className={`${styles.pageLayout} ${isTransitioning ? styles.transitioning : ''}`} key={pageKey}>
            <Suspense fallback={<PageSkeleton />}>
              <OvertimeAccessPage 
                onBack={() => handleNavigate('dashboard')}
              />
            </Suspense>
          </div>
        );

      case 'unregistered-employees':
        return (
          <div className={`${styles.pageLayout} ${isTransitioning ? styles.transitioning : ''}`} key={pageKey}>
            <Suspense fallback={<PageSkeleton />}>
              <UnregisteredEmployeesPage 
                onBack={() => handleNavigate('attendance')}
                onNavigate={handleNavigate}
              />
            </Suspense>
          </div>
        );

      case 'absent-employees':
        return (
          <div className={`${styles.pageLayout} ${isTransitioning ? styles.transitioning : ''}`} key={pageKey}>
            <Suspense fallback={<PageSkeleton />}>
              <AbsentEmployeesPage 
                onBack={() => handleNavigate('attendance')}
                onNavigate={handleNavigate}
                selectedDate={employeeId || new Date().toISOString().split('T')[0]}
              />
            </Suspense>
          </div>
        );

      case 'security-audit-logs':
        return (
          <div className={`${styles.pageLayout} ${isTransitioning ? styles.transitioning : ''}`} key={pageKey}>
            <Suspense fallback={<PageSkeleton />}>
              <SecurityAuditLogsPage 
                onBack={() => handleNavigate('dashboard')}
                onNavigate={handleNavigate}
              />
            </Suspense>
          </div>
        );  
      
      case 'register-user':
        return (
          <div className={`${styles.pageLayout} ${isTransitioning ? styles.transitioning : ''}`} key={pageKey}>
            <Suspense fallback={<PageSkeleton />}>
              <EmployeeListManagementPage 
                onAddEmployee={() => handleNavigate('add-employee-new')}
                onEmployeeClick={handleEmployeeClick}
                onNavigate={handleNavigate}
              />
            </Suspense>
          </div>
        );

      case 'location-exemptions':
        return (
          <div className={`${styles.pageLayout} ${isTransitioning ? styles.transitioning : ''}`} key={pageKey}>
            <Suspense fallback={<PageSkeleton />}>
              <LocationExemptionPage 
                onBack={() => handleNavigate('dashboard')}
                user={user}
              />
            </Suspense>
          </div>
        );

      case 'add-employee-new':
        return (
          <div className={`${styles.pageLayout} ${isTransitioning ? styles.transitioning : ''}`} key={pageKey}>
            <Suspense fallback={<PageSkeleton />}>
              <EmployeeCreationPage 
                onBack={() => handleNavigate('register-user')}
                onSuccess={() => {
                  setTimeout(() => handleNavigate('register-user'), 2000);
                }}
              />
            </Suspense>
          </div>
        );
      
      case 'dashboard':
      default:
        return (
          <div className={`${styles.contentGrid} ${isTransitioning ? styles.transitioning : ''}`} key={pageKey}>
            {dashboardSections}
          </div>
        );
    }
  }, [
    activePage, 
    employeeId, 
    isTransitioning, 
    dashboardSections, 
    handleNavigate, 
    handleEmployeeClick, 
    handleEmployeeVacationClick, 
    user,
    pageKey,
    handleOvertimeReportsClick,
    handleManpowerDashboardClick,
    handleAttendanceClick,
    handleDataAccuracyClick,
    reportCardStyles,
    reportCardHoverHandlers,
    attendanceCardHoverHandlers
  ]);

  return (
    <>
      <main className={`
        ${styles.mainContent} 
        ${isPending ? styles.pending : ''} 
        ${isSidebarCollapsed ? styles.sidebarCollapsed : ''}
      `}>
        {renderPageContent}
      </main>
      
      <FloatingChatButton />
    </> 
  );
});

MainContent.displayName = 'MainContent';
export default MainContent;